import type { AiTag } from '@awork/_shared/models/tag.model'

export type AgentSkillSecretScope = 'workspace' | 'user'
export type AgentSkillType = 'user' | 'agent'

export interface IAgentSkillSecretRequirement {
  id: string
  envVarName: string
  description?: string
  scope: AgentSkillSecretScope
  isRequired: boolean
  hasValue: boolean
}

export interface IAgentSkillSecretValueStatus {
  envVarName: string
  scope: AgentSkillSecretScope
  updatedOn?: string
}

export interface IAgentSkill {
  id: string
  createdBy?: string
  createdOn?: string
  createdWithThreadId?: string
  updatedOn?: string
  key: string
  name: string
  description?: string
  instructions: string
  instructionsSource?: string
  type: AgentSkillType
  userId?: string | null
  agentId?: string | null
  accessLevel?: 'read' | 'manage'
  hasAdminAccessOnly?: boolean
  isDirectlyAccessible?: boolean
  isOwner?: boolean
  workspaceAccessLevel?: 'read' | 'manage' | null
  linkedAgentCount?: number
  metadata?: string
  hasFiles?: boolean
  secretRequirements?: IAgentSkillSecretRequirement[]
  isOfficial?: boolean
  tags?: AiTag[]
  canEditTags?: boolean
  canSetOfficial?: boolean
}

export interface IAgentSkillFile {
  relativePath: string
  sizeBytes: number
}

export interface AgentSkillLink {
  agentId: string
  skillId: string
}

export interface AgentSkillContributor {
  id?: string
  skillId?: string
  userId: string
  accessLevel: 'read' | 'manage'
}

export interface AgentSkillTeamShare {
  id?: string
  skillId?: string
  teamId: string
  accessLevel: 'read' | 'manage'
}

export interface AgentSkillSharing {
  workspaceAccessLevel: 'read' | 'manage' | null
  contributors: AgentSkillContributor[]
  teams: AgentSkillTeamShare[]
}

/**
 * The agent skill model in awork.
 */
export class AgentSkill implements IAgentSkill {
  id: string
  createdBy?: string
  createdOn?: string
  createdWithThreadId?: string
  updatedOn?: string
  key: string
  name: string
  description?: string
  instructions: string
  instructionsSource?: string
  type: AgentSkillType
  userId?: string | null
  agentId?: string | null
  accessLevel?: 'read' | 'manage'
  hasAdminAccessOnly?: boolean
  isDirectlyAccessible?: boolean
  isOwner?: boolean
  workspaceAccessLevel?: 'read' | 'manage' | null
  linkedAgentCount?: number
  metadata?: string
  hasFiles?: boolean
  secretRequirements?: IAgentSkillSecretRequirement[]
  isOfficial: boolean
  tags: AiTag[]
  canEditTags: boolean
  canSetOfficial: boolean

  constructor(skill: Partial<IAgentSkill>) {
    Object.assign(this, skill)
    this.isOfficial = skill.isOfficial ?? false
    this.tags = skill.tags ?? []
    this.canEditTags = skill.canEditTags ?? false
    this.canSetOfficial = skill.canSetOfficial ?? false
  }

  /**
   * Whether the skill is private to a single agent.
   * @returns {boolean}
   */
  get isAgentOwned(): boolean {
    return this.type === 'agent'
  }

  /**
   * Whether the current user may edit and share the skill.
   * @returns {boolean}
   */
  get canManage(): boolean {
    return this.accessLevel === 'manage'
  }
}
